import type { ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";

import { Reveal } from "./Reveal";
import { SmartLink } from "./SmartLink";

/* ────────────────────────────────────────────────────────────────────────
 * Layout primitives.
 *
 * Every section on the site is built from these four, which is what keeps
 * seven pages reading as one document rather than seven templates. The
 * vertical rhythm lives here and nowhere else.
 * ─────────────────────────────────────────────────────────────────────── */

export function Section({
  id,
  children,
  className = "",
  tight,
}: {
  id?: string;
  children: ReactNode;
  className?: string;
  tight?: boolean;
}) {
  return (
    <section
      id={id}
      className={`relative ${tight ? "py-12 sm:py-16" : "py-16 sm:py-24 lg:py-28"} ${className}`}
    >
      {children}
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-white/60">
      {children}
    </div>
  );
}

/**
 * Section heading. `align="left"` is the default because a page where every
 * section is centred reads as a template; centring is reserved for the
 * moments that genuinely are the middle of the argument.
 */
export function SectionHead({
  eyebrow,
  title,
  lede,
  align = "left",
  className = "",
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  align?: "left" | "center";
  className?: string;
}) {
  const centered = align === "center";
  return (
    <Reveal
      className={`${centered ? "mx-auto max-w-3xl text-center" : "max-w-3xl"} ${className}`}
    >
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <h2
        className={`font-display text-[1.9rem] font-bold leading-[1.1] tracking-tight sm:text-4xl lg:text-[2.75rem] ${
          eyebrow ? "mt-4" : ""
        }`}
      >
        {title}
      </h2>
      {lede ? (
        <p
          className={`mt-4 text-[15px] leading-relaxed text-white/65 sm:mt-5 sm:text-lg ${
            centered ? "mx-auto" : ""
          }`}
        >
          {lede}
        </p>
      ) : null}
    </Reveal>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * Buttons. Two variants only. A third would start the drift towards the
 * "template cards everywhere" look the brief rules out.
 * ─────────────────────────────────────────────────────────────────────── */

export function ButtonLink({
  href,
  children,
  variant = "primary",
  className = "",
  icon = true,
}: {
  href: string;
  children: ReactNode;
  variant?: "primary" | "ghost";
  className?: string;
  icon?: boolean;
}) {
  const base =
    "group inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition sm:text-base";
  const styles =
    variant === "primary"
      ? "bg-white text-ink-950 shadow-glow hover:-translate-y-0.5"
      : "border border-white/15 bg-white/[0.04] text-white/90 hover:border-white/30 hover:bg-white/[0.08]";
  return (
    <SmartLink href={href} className={`${base} ${styles} ${className}`}>
      {children}
      {icon ? (
        <ArrowUpRight className="size-4 transition-transform group-hover:rotate-45" />
      ) : null}
    </SmartLink>
  );
}

/**
 * A text link that reads as a next step rather than a button. Used at the
 * foot of cards, where a full button would make three cards look like a
 * pricing table.
 */
export function ArrowLink({
  href,
  children,
  className = "",
}: {
  href: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <SmartLink
      href={href}
      className={`group/al inline-flex items-center gap-1.5 text-sm font-semibold text-white transition-colors hover:text-emerald-300 ${className}`}
    >
      {children}
      <ArrowUpRight className="size-4 transition-transform group-hover/al:translate-x-0.5 group-hover/al:-translate-y-0.5" />
    </SmartLink>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * Card. The site's one container. `interactive` adds the lift + accent
 * bloom on hover; static cards stay flat so that a hoverable card actually
 * signals something.
 * ─────────────────────────────────────────────────────────────────────── */

export function Card({
  children,
  className = "",
  interactive,
  accent,
}: {
  children: ReactNode;
  className?: string;
  interactive?: boolean;
  accent?: "emerald" | "violet";
}) {
  const bloom =
    accent === "violet"
      ? "from-violet-500/40 to-fuchsia-500/30"
      : "from-emerald-500/40 to-teal-500/30";
  return (
    <div
      className={`glass relative overflow-hidden ${
        interactive
          ? "group transition duration-300 hover:-translate-y-1 hover:border-white/20"
          : ""
      } ${className}`}
    >
      {interactive ? (
        <div
          className={`pointer-events-none absolute -inset-px -z-10 rounded-2xl bg-gradient-to-br opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-40 ${bloom}`}
        />
      ) : null}
      {children}
    </div>
  );
}

/** A small labelled capability chip. */
export function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs text-white/70 transition-colors hover:border-white/25 hover:text-white">
      {children}
    </span>
  );
}

/** Status dot + label. Green pulse only for things genuinely in production. */
export function LiveDot({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-200">
      <span className="relative flex size-2">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-70" />
        <span className="relative inline-flex size-2 rounded-full bg-emerald-400" />
      </span>
      {label}
    </span>
  );
}

/** Hairline rule used to separate stacked editorial blocks. */
export function Rule({ className = "" }: { className?: string }) {
  return <div className={`h-px w-full bg-white/10 ${className}`} />;
}
